import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { NonfungiblePositionManager } from '@kodiak-finance/v3-sdk'
import { NONFUNGIBLE_POSITION_MANAGER_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import { useSendTransaction } from 'hooks/useTransaction'
import JSBI from 'jsbi'
import { useCallback, useMemo, useState } from 'react'
import { useIsTransactionPending, useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import { currencyId } from 'utils/currencyId'
import { Hex } from 'viem'

const FEE_COLLECTION_TOLERANCE_BPS = 1 // 0.01% tolerance

function applyTolerance<T extends Currency>(amount: CurrencyAmount<T>): CurrencyAmount<T> {
  if (amount.equalTo(0)) return amount

  const toleranceMultiplier = JSBI.BigInt(10000 - FEE_COLLECTION_TOLERANCE_BPS)
  const divisor = JSBI.BigInt(10000)
  const reduced = JSBI.divide(JSBI.multiply(amount.quotient, toleranceMultiplier), divisor)

  return CurrencyAmount.fromRawAmount(amount.currency, reduced)
}

interface UseCollectFeesParams {
  tokenId?: string
  currency0?: Token
  currency1?: Token
  feeValue0?: CurrencyAmount<Token>
  feeValue1?: CurrencyAmount<Token>
}

export function useCollectFees({ tokenId, currency0, currency1, feeValue0, feeValue1 }: UseCollectFeesParams) {
  const { chainId, account, wagmiPublicClient } = useConnection()
  const { sendTransactionAsync } = useSendTransaction()
  const [collecting, setCollecting] = useState(false)
  const [collectMigrationHash, setCollectMigrationHash] = useState<string | null>(null)
  const isCollectPending = useIsTransactionPending(collectMigrationHash ?? undefined)

  const addTransaction = useTransactionAdder()
  const positionManagerAddress = useMemo(
    () => (chainId ? NONFUNGIBLE_POSITION_MANAGER_ADDRESSES[chainId] : undefined),
    [chainId]
  )

  const canCollect = Boolean(
    currency0 &&
      currency1 &&
      (feeValue0?.greaterThan(0) || feeValue1?.greaterThan(0)) &&
      tokenId &&
      !collectMigrationHash
  )

  const collect = useCallback(async () => {
    if (!currency0 || !currency1 || !chainId || !positionManagerAddress || !account || !tokenId || !wagmiPublicClient) {
      return false
    }

    setCollecting(true)

    try {
      const expectedOwed0 = feeValue0 ?? CurrencyAmount.fromRawAmount(currency0, 0)
      const expectedOwed1 = feeValue1 ?? CurrencyAmount.fromRawAmount(currency1, 0)

      // Apply tolerance to account for fee reader rounding discrepancies
      const tolerantOwed0 = applyTolerance(expectedOwed0)
      const tolerantOwed1 = applyTolerance(expectedOwed1)

      const { calldata, value } = NonfungiblePositionManager.collectCallParameters({
        tokenId,
        expectedCurrencyOwed0: tolerantOwed0,
        expectedCurrencyOwed1: tolerantOwed1,
        recipient: account,
      })

      const txn = {
        to: positionManagerAddress,
        data: calldata as Hex,
        value: BigInt(value),
        account,
      }

      const estimate = await wagmiPublicClient.estimateGas(txn)

      const hash = await sendTransactionAsync({
        to: txn.to,
        data: txn.data,
        gas: calculateGasMargin(estimate),
        value: txn.value,
      })

      setCollectMigrationHash(hash)
      setCollecting(false)

      addTransaction({ hash } as any, {
        type: TransactionType.COLLECT_FEES,
        currencyId0: currencyId(currency0),
        currencyId1: currencyId(currency1),
      })

      return true
    } catch (error) {
      setCollecting(false)
      console.error('Error collecting fees:', error)
      return false
    }
  }, [
    chainId,
    feeValue0,
    feeValue1,
    currency0,
    currency1,
    positionManagerAddress,
    account,
    tokenId,
    addTransaction,
    wagmiPublicClient,
    sendTransactionAsync,
  ])

  const resetCollectState = useCallback(() => {
    setCollectMigrationHash(null)
    setCollecting(false)
  }, [])

  return {
    collect,
    collecting,
    collectMigrationHash,
    isCollectPending,
    canCollect,
    resetCollectState,
    isCollected: !!collectMigrationHash && !isCollectPending,
  }
}
